import { VestigingFormContext } from "../../../../Vestigingen/VestigingForm";
import { useFieldArray, useFormContext } from "react-hook-form";
import TranslationWrapper from "../../../../../components/TranslationWrapper/TranslationWrapper";
import {
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Delete, Visibility } from "@mui/icons-material";
import { Link } from "react-router";
import Tooltip from "@mui/material/Tooltip";
import Button from "../../../../../components/CustomButtons/Button";
import React from "react";
import FormSelect from "../../../../../components/Forms/FormSelect";
import { arrangementCreditCostOptions } from "../../../../Upsell/modernTS/JUI/Arrangements/arrangementCreditCostSelectOptions";
import { FormattedNumber } from "react-intl";
import { AutocompleteProduct } from "@jamezz/react-components";
import {
  ProductAttributes,
  useLazyFetchProductsJsonQuery,
} from "../../../../../../../Domains/Products/api/productJsonApi.ts";
import { RenderOption } from "../../../../OptionGroups/Tabs/AutocompleteProductRenderOption.tsx";

interface ProductResponse {
  products: {
    naam: string;
    value: string;
    label: string;
    id: string;
    prijs: string;
  }[];
}

export function ProductSelectionTable() {
  const { control, menukaartVestigingId, vestiging }: VestigingFormContext = useFormContext<
    ProductResponse & VestigingFormContext
  >();
  const { fields, replace, remove, update } = useFieldArray<ProductResponse>({
    control,
    name: "products",
    keyName: "value",
  });

  return (
    <>
      <AutocompleteProduct<true, ProductAttributes, true>
        multiple={true}
        menuSalesareaId={menukaartVestigingId}
        fetchQuery={useLazyFetchProductsJsonQuery}
        renderOption={(props, option) => <RenderOption option={option} />}
        value={(fields ?? []).map((field) => {
          field.value = field.id = field.id ?? field.value;
          field.label = field.naam = field.naam ?? field.label;
          return String(field.value);
        })}
        onChange={(value) => {
          replace(
            value.map((product) => {
              return {
                key: product.key,
                ...product.attributes,
                id: Number(product.id),
                value: Number(product.id),
                label: product.naam,
                pivot: { credit_cost: null },
              };
            })
          );
        }}
      />
      {fields.length > 0 ? (
        <TableContainer sx={{ mt: 2 }}>
          <Table size={"small"}>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>
                  <TranslationWrapper tDefault={"Price"} tKey={"Price"} />
                </TableCell>
                <TableCell>
                  <TranslationWrapper tDefault={"Name"} tKey={"Name"} />
                </TableCell>
                <TableCell>
                  <TranslationWrapper tDefault={"Credit cost"} tKey={"Arrangements.credit_cost"} />
                </TableCell>
                <TableCell>
                  <TranslationWrapper tDefault={"Actions"} tKey={"Actions"} />
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody
              sx={{
                "& tr:nth-child(odd)": {
                  backgroundColor: "#eee",
                },
              }}
            >
              {fields.map((field, index) => {
                return (
                  <TableRow key={field.id}>
                    <TableCell>
                      <Typography sx={{ color: "#555" }}>#{field.id}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>
                        <FormattedNumber currency={vestiging?.valuta ?? "EUR"} style={"currency"} value={field.prijs} />
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography>{field.naam}</Typography>
                    </TableCell>
                    <TableCell>
                      <FormSelect
                        InputProps={{
                          placeholder: "Overwrite credit cost",
                        }}
                        defaultValue={null}
                        name={`products[${index}].pivot.credit_cost`}
                        onSelect={(c) => {
                          if (c !== field.pivot?.credit_cost) {
                            update({
                              ...field,
                              pivot: {
                                ...field.pivot,
                                credit_cost: c,
                              },
                            });
                          }
                        }}
                        options={[
                          <MenuItem key={"null"} value={null}>
                            <TranslationWrapper tDefault={"Unset"} tKey={"Arrangements.credit_cost.unset"} />
                          </MenuItem>,
                          ...arrangementCreditCostOptions({ compact: true }),
                        ]}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          remove(index);
                        }}
                      >
                        <Delete />
                      </IconButton>

                      <Link to={`/manager/producten/${field.id}/edit`}>
                        <Tooltip
                          placement={"bottom"}
                          title={
                            <TranslationWrapper tDefault={"View Product"} tKey={"global.jamezzTable.viewProduct"} />
                          }
                        >
                          <Button color={"success"} simple>
                            <Visibility />
                          </Button>
                        </Tooltip>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      ) : null}
    </>
  );
}
